/**
 * Budget Pay pure rules — Live TV COA (budget-pay-bp-rules.js).
 * Keep in sync with Budget Pay app assets/bp-rules.js and shared/bp-rules.mjs.
 */

export const DEFAULT_BP_GUEST_LIMIT = 1000;
export const BP_MIN_PROPERTY_KEY = '_bp_min_installments';
export const BP_PLANS_PROPERTY_KEY = '_bp_plans';

export function hasBudgetPayLineProperties(properties) {
  if (!properties) return false;
  const min = properties[BP_MIN_PROPERTY_KEY];
  const plans = properties[BP_PLANS_PROPERTY_KEY];
  return (min != null && min !== '') || (plans != null && plans !== '');
}

export function isBudgetPayCartLine(line) {
  if (!line?.selling_plan_allocation) return false;
  return hasBudgetPayLineProperties(line?.properties);
}

export function parseInstallmentCountFromPlanName(name) {
  if (!name || typeof name !== 'string') return null;
  const parts = name.trim().split(/\s+/);
  for (const part of parts) {
    const n = parseInt(part, 10);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return null;
}

export function getBudgetPayCartLines(cart) {
  const items = cart?.items ?? [];
  return items.filter((item) => isBudgetPayCartLine(item));
}

export function parsePlansMapFromProperties(properties) {
  if (!properties) return {};
  const raw = properties[BP_PLANS_PROPERTY_KEY];
  if (!raw) return {};
  try {
    const parsed = JSON.parse(String(raw));
    if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return {};
}

export function getMinInstallmentsFromLine(line) {
  const props = line?.properties ?? {};
  const fromProp = props[BP_MIN_PROPERTY_KEY];
  if (fromProp != null && fromProp !== '') {
    const n = parseInt(String(fromProp), 10);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return getLinePlanCount(line);
}

export function getProductMinInstallmentsFromLine(line) {
  const props = line?.properties ?? {};
  const fromProp = props[BP_MIN_PROPERTY_KEY];
  if (fromProp == null || fromProp === '') return null;
  const n = parseInt(String(fromProp), 10);
  if (!Number.isNaN(n) && n > 0) return n;
  return null;
}

export function getLinePlanCount(line) {
  const name = line?.selling_plan_allocation?.selling_plan?.name;
  return parseInstallmentCountFromPlanName(name);
}

export function getLinePlanCountOrMin(line) {
  const fromPlan = getLinePlanCount(line);
  if (fromPlan != null) return fromPlan;
  return getMinInstallmentsFromLine(line);
}

export function getTargetInstallmentCount(bpLines, getMinForLine, extraMins = []) {
  const mins = [...extraMins];
  for (const line of bpLines) {
    const min = getMinForLine(line);
    if (min != null && min > 0) mins.push(min);
  }
  if (mins.length === 0) return 0;
  return Math.max(...mins);
}

export function getLineFinancedTotalCents(line) {
  const qty = Math.max(1, Math.floor(Number(line?.quantity ?? 1)) || 1);
  const finalLine = Number(line?.final_line_price ?? 0);
  const originalLine = Number(line?.original_line_price ?? 0);
  const originalUnit = Number(line?.original_price ?? 0);
  const hasBpPlan = isBudgetPayCartLine(line);
  const planCount = getLinePlanCountOrMin(line);

  if (Number.isFinite(originalLine) && originalLine > 0) {
    return Math.round(originalLine);
  }

  if (Number.isFinite(originalUnit) && originalUnit > 0) {
    return Math.round(originalUnit * qty);
  }

  if (!Number.isFinite(finalLine) || finalLine <= 0) return 0;

  if (hasBpPlan && planCount != null && planCount > 1) {
    return Math.round(finalLine * planCount);
  }

  return Math.round(finalLine);
}

export function computeBpExposureCents(lines) {
  let total = 0;
  for (const line of lines) {
    const financed = getLineFinancedTotalCents(line);
    if (financed > 0) total += financed;
  }
  return { ok: true, exposureCents: total, message: '' };
}

export function computeBpExposureAtTargetCount(bpLines, _targetCount, addedLineFullPriceCents = 0) {
  let total = 0;
  const added = Number(addedLineFullPriceCents);
  if (Number.isFinite(added) && added > 0) {
    total += Math.round(added);
  }
  for (const line of bpLines) {
    const financed = getLineFinancedTotalCents(line);
    if (financed > 0) total += financed;
  }
  return { ok: true, exposureCents: total, message: '' };
}

export function validateEligibility(eligibility, exposureCents) {
  if (!eligibility.available) {
    return { ok: false, message: 'You have an outstanding Budget Pay balance. Please clear it to use Budget Pay again, or you can choose to pay in full.' };
  }
  const creditLimitCents = Math.round(eligibility.creditLimit * 100);
  if (exposureCents > creditLimitCents) {
    return {
      ok: false,
      message: `Budget Pay limit exceeded. Your limit is ${eligibility.creditLimit.toFixed(2)}.`,
    };
  }
  return { ok: true, message: '' };
}

export function linesNeedingSync(bpLines, targetCount, getPlanCountForLine) {
  return bpLines.filter((line) => {
    const count = getPlanCountForLine(line);
    return count != null && count !== targetCount;
  });
}

export function resolvePlanForCartLine(line, targetCount, resolveFromDom) {
  const key = String(targetCount);
  const plans = parsePlansMapFromProperties(line?.properties);
  if (plans[key] != null) return plans[key];
  if (plans[targetCount] != null) return plans[targetCount];
  const handle = line?.handle;
  if (handle && resolveFromDom) {
    return resolveFromDom(targetCount, handle);
  }
  return null;
}
